import {
  CircularProgress,
  Grid,
  Button,
  Typography,
  Box,
  Container,
} from "@mui/material";
import jwtDecode from "jwt-decode";
import moment from "moment";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useAlert } from "react-alert";
import { useSearchParams } from "react-router-dom";
import { Form, Formik, FormikHelpers } from "formik";
import * as yup from "yup";
import FormikInput from "../../shared/components/FormikInput";
import UploadFiles, { FileInputRef } from "../HireV3/UploadFiles";
import { useSubmitAssessmentMutation } from "../../redux/Assessment/assessmentApis";
import { useGetEmailRefMutation } from "../../redux/EmailRef/emailRefApis";

const validationSchema = yup.object({
  notes: yup.string().optional(),
});

type DecodedToken = {
  assessmentId: string;
  attachmentList: string;
  aud: string;
  candidateEmail: string;
  candidateId: string;
  candidateName: string;
  exp: number;
  isPassed: string;
  iss: string;
  jobId: string;
  nbf: number;
  stage: string;
};

const AssessmentSubmission = () => {
  const ref = useRef<FileInputRef>(null);
  const formRef = useRef<any>(null);
  const alert = useAlert();
  const [queryParams] = useSearchParams();
  const [decodedToken, setDecodedToken] = useState<DecodedToken | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [displayMsg, setDisplayMsg] = useState<null | string>(null);

  const assessmentRefToken = queryParams.get("ref") ?? "";
  const [submitAssessment, submitAssessmentMutating] =
    useSubmitAssessmentMutation();
  const [getRefToken] = useGetEmailRefMutation();

  const setRefData = async () => {
    if (!assessmentRefToken) return;
    const tokenData = await getRefToken({
      refId: assessmentRefToken,
    });
    setIsLoading(false);
    if (tokenData.error) {
      setDisplayMsg("Something went wrong, Please try again");
      alert.error("Something went wrong !!");
    }
    const decodedToken: DecodedToken = jwtDecode(tokenData.data?.data.data);
    if (decodedToken.isPassed === "True")
      setDisplayMsg(`You have already submitted the assessment`);

    setDecodedToken(decodedToken);
  };

  const submitHandler = useCallback(
    (data, formikHelper: FormikHelpers<any>) => {
      //Before submitting validations
      if (!data.notes && !ref.current?.files.length) {
        alert.error("Please add notes OR upload files");
        return;
      }
      // Handle form submission logic
      submitAssessment({
        assessmentId: decodedToken?.assessmentId ?? "",
        stage: decodedToken?.stage ?? "",
        candidateId: decodedToken?.candidateId ?? "",
        jobId: decodedToken?.jobId ?? "",
        submissionText: data.notes,
        attachmentList: ref.current?.files,
      });
    },
    [decodedToken],
  );

  //Handle Submit reponse
  useEffect(() => {
    if (submitAssessmentMutating.isSuccess) {
      alert.success("Assessment submitted successfully");
      setDisplayMsg(`Thank you for submitting your assessment. Our team will review it and get back to you shortly`);
      ref.current?.clearInput();
      formRef.current?.resetForm();
    }
    if (submitAssessmentMutating.isError) {      
      //@ts-ignore
      if (submitAssessmentMutating?.error?.data?.status === 409) {
        alert.error(`You have already submitted the assessment`);
      } else {
        alert.error("Error while submitting assessment");
      }
    }
  }, [submitAssessmentMutating]);

  //Get the data from token
  useEffect(() => {
    setRefData();
  }, [assessmentRefToken]);

  const TeamifiedHeader = () => (
    <Container maxWidth={false} style={{ backgroundColor: "black" }}>
      <Box
        component="img"
        alt="loading...."
        src="/images/Teamified-Logo-White.svg"
        sx={{ width: "225px" }}
        padding={3}
        paddingLeft="17%"
      />
    </Container>
  );

  if (isLoading || displayMsg) {
    return (
      <>
        <TeamifiedHeader />
        <Grid
          container
          item
          justifyContent={"center"}
          alignItems={"center"}
          marginTop={10}
        >
          {displayMsg ? (
            <Typography variant="h6" color="orange">
              {displayMsg}
            </Typography>
          ) : (
            <CircularProgress size={42} />
          )}
        </Grid>
      </>
    );
  }

  return (
    <>
      <TeamifiedHeader />
      <Grid container spacing={0} paddingX="17%">
        <Grid item xs={9} md={9} paddingY={7}>
          <Typography variant="h2">Assessment</Typography>
        </Grid>
        <Grid
          item
          xs={3}
          md={3}
          paddingY={7}
          alignSelf="center"
          textAlign="right"
        >
          <Typography variant="h6">{moment().format("DD-MMM-YY")}</Typography>
        </Grid>
        <Formik
          initialValues={{
            notes: "",
            attachmentFiles: [],
          }}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={submitHandler}
          innerRef={formRef}
        >
          {(formikProps) => {
            return (
              <Form style={{ display: "contents" }}>
                <Grid item xs={12} md={12} paddingY={2}>
                  <Typography variant="subtitle1">
                    Add notes or paste a link to test submission
                  </Typography>
                </Grid>
                <Grid item xs={12} md={12}>
                  <FormikInput
                    label="Enter Notes / URL"
                    name="notes"
                    options={undefined}
                    formikProps={formikProps}
                    fullWidth
                    rows={4}
                    multiline
                    inputType="textarea"
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={3}
                  alignItems="center"
                  display="baseline"
                  paddingY={"10px"}
                >
                  <Typography variant="subtitle1">Add attachement</Typography>
                </Grid>
                <Grid item xs={12} md={9}>
                  <UploadFiles
                    label={"Upload files*"}
                    name="attachmentFiles"
                    isMultiSelect={true}
                    ref={ref}
                  />
                </Grid>
                <Grid item xs={12} md={12} alignItems="flex-end">
                  <Box textAlign="right" mt={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      sx={{ height: 40 }}
                      disabled={submitAssessmentMutating.isLoading}
                    >
                      {submitAssessmentMutating.isLoading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Grid>
    </>
  );
};

export default memo(AssessmentSubmission);
